import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'antd';
import styled from 'styled-components';
import { AntdTable } from 'components/custom-table/Style';
import { numberWithCommas, rateWithCommas } from 'utils/NumberCommaSeprator';

const money = (value) => `₹ ${numberWithCommas(Math.round(Number(value) || 0))}`;
// The slab rate keeps its real precision — rounding it made the walk disagree with the
// rate the revenue was actually billed at.
const rate = (value) => `₹ ${rateWithCommas(value)}`;

// Past a handful of slabs the modal would grow past the viewport, so the body gets a
// fixed height and scrolls. The cap is in pixels, so it fits ~5 two-line rows (a
// partly-filled slab carries a second line) or ~7 single-line ones.
const SCROLL_AFTER_ROWS = 5;
const ROW_HEIGHT = 54;
const num = (value) => numberWithCommas(Number(value) || 0);

const Num = styled.span`
	display: block;
	text-align: right;
	font-variant-numeric: tabular-nums;
`;

const Note = styled.div`
	font-size: 12px;
	color: #8c8c8c;
`;

const columns = [
	{
		title: 'Slab',
		key: 'slab',
		render: (_, record) => {
			// Whether this row used the whole slab or only part of it. A partial row is
			// the normal case for a single day, which picks up mid-slab where the days
			// before it stopped.
			const usedWholeSlab =
				record.from_count <= (record.slab_min || 1) && record.to_count >= record.slab_max;
			return (
				<>
					<div>
						{num(record.slab_min)} – {num(record.slab_max)}
					</div>
					{!usedWholeSlab && (
						<Note>
							shipments {num(record.from_count)} – {num(record.to_count)} of the month
						</Note>
					)}
				</>
			);
		},
	},
	{
		title: 'Rate',
		dataIndex: 'rate_per_order',
		key: 'rate_per_order',
		align: 'right',
		render: (value) => <Num>{rate(value)}</Num>,
	},
	{
		title: 'Shipments',
		dataIndex: 'shipment_count',
		key: 'shipment_count',
		align: 'right',
		render: (value) => <Num>{num(value)}</Num>,
	},
	{
		title: 'Amount',
		dataIndex: 'amount',
		key: 'amount',
		align: 'right',
		render: (value) => <Num>{money(value)}</Num>,
	},
];

/**
 * How a shipment count was billed, slab by slab.
 *
 * Slabs are consumed over a billing month and each portion is billed at its own
 * slab's rate, so a count of 150 against slabs 1–100 @ ₹18 and 101–200 @ ₹7 is
 * two rows, not one — this is that walk, made visible.
 *
 * Rows come from the backend's revenue engine (compute_per_order_breakdown), which
 * is the same walk that produces the total being explained. Nothing is recomputed
 * here, so this table cannot drift from the figure it sits under.
 */
export default function SlabBreakdownTable({ breakdown, shipmentCount, totalLabel }) {
	const rows = breakdown || [];
	const billed = rows.reduce((sum, row) => sum + (Number(row.shipment_count) || 0), 0);
	const total = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
	// Volume past the top slab earns nothing, so the rows can account for fewer
	// shipments than were entered. Say so rather than letting the totals differ
	// silently.
	const unbilled = Math.max(0, (Number(shipmentCount) || 0) - billed);

	return (
		<>
			<AntdTable
				rowKey={(record, index) => `slab-row-${index}`}
				size="small"
				columns={columns}
				dataSource={rows}
				pagination={false}
				// A project can define many slabs; past ~5 rows the modal would grow past
				// the viewport. Scroll the body instead — the header and the Total row are
				// fixed, so what the rows add up to stays on screen while scrolling.
				scroll={
					rows.length > SCROLL_AFTER_ROWS
						? { y: SCROLL_AFTER_ROWS * ROW_HEIGHT }
						: undefined
				}
				// antd's summary API takes a render function by design
				// eslint-disable-next-line react/no-unstable-nested-components
				summary={() => (
					<Table.Summary fixed>
						<Table.Summary.Row>
							<Table.Summary.Cell index={0}>
								<strong>{totalLabel}</strong>
							</Table.Summary.Cell>
							{/* Rate: no total to show — each slab has its own rate */}
							<Table.Summary.Cell index={1} />
							<Table.Summary.Cell index={2} align="right">
								<Num>
									<strong>{num(billed)}</strong>
								</Num>
							</Table.Summary.Cell>
							<Table.Summary.Cell index={3} align="right">
								<Num>
									<strong>{money(total)}</strong>
								</Num>
							</Table.Summary.Cell>
						</Table.Summary.Row>
					</Table.Summary>
				)}
			/>

			{unbilled > 0 && (
				<Note style={{ marginTop: 12 }}>
					{num(unbilled)} shipment{unbilled === 1 ? '' : 's'} above the top slab earned
					nothing — add a higher slab in Project Setup to bill them.
				</Note>
			)}
		</>
	);
}

SlabBreakdownTable.propTypes = {
	breakdown: PropTypes.array,
	shipmentCount: PropTypes.number,
	totalLabel: PropTypes.string,
};

SlabBreakdownTable.defaultProps = {
	breakdown: [],
	shipmentCount: 0,
	totalLabel: 'Total',
};
